<template>
    <popup-modal
        position="center"
        customClasses="w-672 max-w-full"
        :title="$t('mailer.add_recipients')"
        @close="$emit('close')"
    >
        <div class="mb-12">
            <div class="flex justify-between gap-5">
                <div class="grow flex flex-col">
                    <simple-input
                        class="grow"
                        id="applicantName"
                        icon="assignment-ind"
                        icon-position="left"
                        :input-label="$t('applications.search_by_name')"
                        name="applicantName"
                        :placeholder="$t('users.name')"
                        :value="filter.text_keyword"
                        @input="handleSearch($event, 'text_keyword')"
                    />
                </div>
                <div class="grow flex flex-col">
                    <select-dropdown
                        :auto-translate="false"
                        class="grow-0"
                        :deselectable="true"
                        :input-label="$t('applications.search_by_job')"
                        :options="jobs"
                        :translatable="true"
                        :value="filter.job"
                        @change="handleSearch($event, 'job')"
                    />
                </div>
            </div>
            <div class="mt-10 border-t border-b">
                <div class="py-12 flex flex-col space-y-8 h-240 max-h-240 overflow-y-scroll scroll-styled text-center">
                        <div
                            v-for="application in filteredApplications"
                            :key="application.id"
                            @click="checkApplicationLocal(application)"
                            class="flex items-center justify-start"
                        >
                            <div class="bg-white rounded border overflow-hidden cursor-pointer grow p-6 flex items-center justify-between">
                                <checkbox
                                    :checked="isCheckedAlready(application)"
                                    class="mr-12"
                                    @change="checkApplicationLocal(application)"
                                />
                                <div class="inline-flex items-center grow">
                                    <avatar
                                        :color="application.stageColor"
                                        :image="application.image_url"
                                        size="small"
                                    >
                                        {{
                                            application.initials
                                        }}
                                    </avatar>
                                    <span class="me-4 ms-6">{{
                                            application.fullName
                                        }}</span>
                                </div>
                                <tooltip
                                    wrapper-class="border-l-2 p-6 max-w-1/3 w-1/3"
                                    :content="applicationTitle(application) ?? $t('general.missing_translation') + ' (' + locale + ')'"
                                    :position="'top'"
                                    :delayed="true"
                                >
                                    <p
                                        v-if="applicationTitle(application)"
                                        class="text-primary-80 text-end whitespace-nowrap"
                                    >
                                        {{ applicationTitle(application) }}
                                    </p>
                                    <label-with-missing-translation
                                        v-else
                                        class="text-primary-80 whitespace-nowrap"
                                    />
                                </tooltip>
                            </div>
                        </div>
                        <infinite-loading
                            ref="infiniteLoading"
                            :slots="{complete: ' ', error: ' '}"
                            @infinite="infiniteHandler"
                        >
                            <template #spinner>
                                <loading size="small"></loading>
                            </template>
                            <template #complete>
                                <span>{{ $t('applications.no_applications_found') }}</span>
                            </template>
                        </infinite-loading>
            </div>
                <div
                    v-if="applicationsToCheck.length"
                    ref="selectedApplications"
                    class="relative border-t mt-12"
                >
                    <p class="absolute top-0 start-0 -translate-y-1/2 ml-12 bg-white px-6 text-primary-80">
                        {{ $t('applications.selected_candidates') }}
                    </p>
                    <div class="max-h-168 space-y-8 overflow-y-scroll scroll-styled snap-y mt-16 mb-10">
                        <div
                            class="flex items-center justify-start snap-end"
                            :key="application.id"
                            v-for="application in applicationsToCheck"
                        >
                        <div
                            class="bg-white rounded border overflow-hidden cursor-pointer grow p-6 flex items-center justify-between"
                            @click="checkApplicationLocal(application)"
                        >
                            <checkbox
                                :checked="isCheckedAlready(application)"
                                class="mr-12"
                                @change="checkApplicationLocal(application)"
                            />
                            <div class="inline-flex items-center grow">
                                <avatar
                                    :color="application.stageColor"
                                    :image="application.image_url"
                                    size="small"
                                >
                                    {{
                                        application.initials
                                    }}
                                </avatar>
                                <span class="me-4 ms-6">{{
                                        application.fullName
                                    }}</span>
                            </div>
                            <tooltip
                                wrapper-class="border-l-2 p-6 max-w-1/3 w-1/3"
                                :content="applicationTitle(application) ?? $t('general.missing_translation') + ' (' + locale + ')'"
                                :position="'top'"
                                :delayed="true"
                            >
                                <p
                                    v-if="applicationTitle(application)"
                                    class="text-primary-80 text-end whitespace-nowrap"
                                >
                                    {{ applicationTitle(application) }}
                                </p>
                                <label-with-missing-translation
                                    v-else
                                    class="text-primary-80 whitespace-nowrap"
                                />
                            </tooltip>
                        </div>
                    </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="flex items-center justify-end gap-12">
            <simple-button
                look="primary"
                @click="closeModal"
            >
                {{ $t("general.cancel") }}
            </simple-button>
            <simple-button
                display="secondary"
                icon="save"
                look="primary"
                @click="saveRecipients"
            >
                {{ $t("general.save") }}
            </simple-button>
        </div>
    </popup-modal>
</template>

<script>
import Avatar from "../../ui/Avatar.vue";
import Checkbox from "../../forms/Checkbox.vue";
import SimpleButton from "../../ui/SimpleButton.vue";
import Input from "../../forms/Input.vue";
import PopupModal from "../../ui/PopupModal.vue";
import styles from "../../../../../../../styles.config";
import * as applicationsService from "../../../services/ApplicationService";
import * as jobService from "../../../services/JobService"
import {normalizeJson} from "../../../../shared/helpers";
import ApplicationModel from "../../../models/Application";
import JobModel from "../../../models/Job";
import {mapState} from "pinia";
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../../../state/store';
import SelectDropdown from "../../forms/SelectDropdown.vue";
import Loading from "../../ui/Loading.vue";
import SvgIcon from "../../ui/SvgIcon.vue";
import Tooltip from "../../navigation/Tooltip.vue";
import infiniteLoading from 'v3-infinite-loading/lib/v3-infinite-loading.es.js';
import LabelWithMissingTranslation from "../../LabelWithMissingTranslation.vue";

export default {
    name: "PopupAddRecipient",
    components: {
        LabelWithMissingTranslation,
        Tooltip,
        SvgIcon,
        Loading,
        SelectDropdown,
        PopupModal,
        'simple-input': Input,
        SimpleButton,
        Checkbox,
        Avatar,
        infiniteLoading,
    },
    props: {
        tempApplications: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            filter: {
                text_keyword: '',
                job: '',
            },
            page: 1,
            filteredApplications: [],
            applicationsToCheck: [],
            loading: false,
            offset: 0,
            timeout: null,
        }
    },
    computed: {
        ...mapState(useMainStore, ['locale']),
        styles() {
            return styles
        },
        jobs() {
            return useRepo(JobModel).all().map(job => {
                return {
                    value: job.id,
                    item: job,
                    label: job.title,
                }
            });
        },
        applicationsCount() {
            return this.filteredApplications.length;
        },
    },
    methods: {
        debounce(func, wait) {
            clearTimeout(this.timeout);
            this.timeout = setTimeout(func, wait);
        },
        handleSearch(input, field) {
            this.offset = 0;
            this.filter[field] = input;
            this.debounce(() => this.fetchApplications(), 500);
        },
        closeModal() {
            this.$emit('close')
        },
        saveRecipients() {
            this.$emit('save', this.applicationsToCheck)
        },
        infiniteHandler(state) {
            this.fetchApplications(true).then(inserts => {
                if (inserts && inserts.length) {
                    this.filteredApplications = [...this.filteredApplications, ...inserts];
                    state.loaded();
                } else {
                    state.complete();
                }
            })
        },
        fetchApplications(withInfinite = false) {
            const urlParams = {};
            if (this.filter.text_keyword) {
                urlParams['filter[keyword]'] = this.filter.text_keyword;
            }
            if (this.filter.job) {
                urlParams['filter[job_id]'] = this.filter.job;
            }
            return applicationsService.index({
                sort: '-created_at',
                extraParameters: urlParams,
                offset: this.offset,
                limit: 7,
                selectedApplications: this.tempApplications.map(application => application.id),
            }).then(result => {
                let normalizedApplications = normalizeJson(result.data);

                // now check if any of the applications is already in the ApplicationModel, if not, add it
                normalizedApplications.forEach(application => {
                    if (!useRepo(ApplicationModel).find(application.id)) {
                        useRepo(ApplicationModel).save(application);
                    }
                })
                this.offset += normalizedApplications.length;
                if (withInfinite) {
                    // now find all the applications from the response in the ApplicationModel, and put them in the filteredApplications
                    return normalizedApplications.map(application => new ApplicationModel(application));
                }
                this.filteredApplications = normalizedApplications.map(application => new ApplicationModel(application));
            })
        },
        fetchJobs() {
            jobService.index({
                filters: {
                    published: 'both',
                    active: 1,
                },
                all: 1,
            }).then(response => {
                const normalizedJobs = normalizeJson(response.data);
                if (normalizedJobs) {
                    useRepo(JobModel).save(normalizedJobs);
                }
            });
        },
        applicationTitle(application) {
            if (application?.stage?.job?.title) {
                return application.stage.job.title[this.locale] || application.stage.job.title['en'] || null;
            } else {
                return null;
            }
        },
        isCheckedAlready(application) {
            return this.applicationsToCheck.some(i => i.id === application.id);
        },
        checkApplicationLocal(application) {
            if (this.applicationsToCheck.find(i => i.id === application.id)) {
                this.applicationsToCheck = this.applicationsToCheck.filter(i => i.id !== application.id);
            } else {
                this.applicationsToCheck = [...this.applicationsToCheck, application];
            }
            this.$nextTick(() => {
                if (this.applicationsToCheck.length) {
                    setTimeout(() => {
                        this.$refs.selectedApplications.scrollTop = this.$refs.selectedApplications.scrollHeight;
                    }, 0);
                }
            })
        },
    },
    mounted() {
        // fetch all the jobs
        this.fetchJobs();
    },
}
</script>


<style scoped>


</style>
